import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import qs from 'query-string';

import getEnvConfig from '../../../../../utils/config/env';
import {
    CloudPaymentsProps,
    PaymentSystemName,
    PaymentSystemProps,
    RobokassaResponse,
    UnitpayKeysResponse,
    UnitpayMetatagResponse,
    YookassaResponse,
} from './paymentSettings.interface';

export const paymentSettingsApi = createApi({
    reducerPath: 'paymentSettings',
    baseQuery: fetchBaseQuery({
        baseUrl: getEnvConfig().env.API_URL,
        prepareHeaders: headers => {
            const token = localStorage?.getItem('accessToken');
            headers.set('Authorization', `Bearer ${token}`);
            return headers;
        },
    }),
    tagTypes: [
        'PaymentSettings',
        'Unitpay',
        'Robokassa',
        'Yookassa',
        'Cloudpayments',
    ],

    endpoints: builder => ({
        readPaymentSystems: builder.query<PaymentSystemProps[], number>({
            query: id => {
                return {
                    url: `/shops/get_payment_systems/${id}`,
                    method: 'GET',
                };
            },
            transformResponse: (res: { data: PaymentSystemProps[] }) =>
                res.data,
            providesTags: ['PaymentSettings'],
        }),
        updatePaymentSystems: builder.mutation<
            any,
            { id: number; payment_system: PaymentSystemName }
        >({
            query: body => {
                return {
                    url: `/shops/set_payment_system/${body.id}`,
                    method: 'POST',
                    body: body,
                };
            },

            invalidatesTags: ['PaymentSettings'],
        }),

        ///UNITPAY
        createUnitPayKeys: builder.mutation<
            UnitpayKeysResponse,
            UnitpayKeysResponse
        >({
            query: body => ({
                url: '/user/unitpay-key',
                method: 'POST',
                body: body,
            }),
            transformResponse: (res: UnitpayKeysResponse) => res,
            invalidatesTags: ['Unitpay'],
        }),
        readUnitPayKeys: builder.query<
            UnitpayKeysResponse,
            { shop_id: number }
        >({
            query: args => {
                const queryArgs = qs.stringify(args, {
                    arrayFormat: 'bracket',
                    skipEmptyString: true,
                    skipNull: true,
                });
                return {
                    url: `/user/unitpay-key?${queryArgs}`,
                    method: 'GET',
                };
            },
            transformResponse: (res: { data: UnitpayKeysResponse }) =>
                res?.data,
            providesTags: ['Unitpay'],
        }),
        createUnitPayMetatag: builder.mutation<void, UnitpayMetatagResponse>({
            query: body => ({
                url: '/shop/unitpay-metatag',
                method: 'POST',
                body: body,
            }),
            invalidatesTags: ['Unitpay'],
        }),
        readUnitMetatag: builder.query<
            UnitpayMetatagResponse,
            { shop_id: number }
        >({
            query: args => {
                const queryArgs = qs.stringify(args, {
                    arrayFormat: 'bracket',
                    skipEmptyString: true,
                    skipNull: true,
                });
                return {
                    url: `/shop/unitpay-metatag?${queryArgs}`,
                    method: 'GET',
                };
            },
            transformResponse: (res: { data: UnitpayMetatagResponse }) =>
                res?.data,
            providesTags: ['Unitpay'],
        }),
        ///ROBO
        readRobokassa: builder.query<RobokassaResponse, number>({
            query: id => {
                return {
                    url: `/robokassa-keys/${id}`,
                    method: 'GET',
                };
            },
            transformResponse: (res: { data: RobokassaResponse }) => res.data,
            providesTags: ['Robokassa'],
        }),
        updateRobokassa: builder.mutation<void, any>({
            query: body => ({
                url: '/robokassa-keys',
                method: 'PUT',
                body: body,
            }),
            invalidatesTags: ['Robokassa'],
        }),
        ///YOOKASSSA
        readYookassaOAuth: builder.query<YookassaResponse, { shop_id: number }>(
            {
                query: args => {
                    const queryArgs = qs.stringify(args, {
                        arrayFormat: 'bracket',
                        skipEmptyString: true,
                        skipNull: true,
                    });
                    return {
                        url: `/yookassa/get_oauth_link?${queryArgs}`,
                        method: 'GET',
                    };
                },
                transformResponse: (res: { data: YookassaResponse }) =>
                    res?.data,
                providesTags: ['Yookassa'],
            }
        ),
        ///CLOUDPAYMENTS
        updateCloudPayments: builder.mutation<void, CloudPaymentsProps>({
            query: body => ({
                url: '/cloud-payments/keys',
                method: 'POST',
                body: body,
            }),
            invalidatesTags: ['Cloudpayments'],
        }),
        readCloudPayments: builder.query<CloudPaymentsProps, number>({
            query: id => {
                return {
                    url: `/cloud-payments/keys/${id}`,
                    method: 'GET',
                };
            },
            transformResponse: (res: { data: CloudPaymentsProps }) => res?.data,
            providesTags: ['Cloudpayments'],
        }),
    }),
});

export const {
    /*UNITPAY*/
    useCreateUnitPayKeysMutation,
    useLazyReadUnitPayKeysQuery,
    useCreateUnitPayMetatagMutation,
    useLazyReadUnitMetatagQuery,
    /*ROBO*/
    useLazyReadRobokassaQuery,
    useUpdateRobokassaMutation,
    /*YOOKASSA*/
    useLazyReadYookassaOAuthQuery,
    /*CLOUDPAYMENTS*/
    useLazyReadCloudPaymentsQuery,
    useUpdateCloudPaymentsMutation,
    /*PAYMENT SYSTEM*/
    useLazyReadPaymentSystemsQuery,
    useUpdatePaymentSystemsMutation,
} = paymentSettingsApi;
